import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import ImageViewer from "./ImageViewer";
import pickleballIcon from "./pickleball.png";
import ReportModal from "./components/ReportModal";
import TeamSelectModal from "./components/TeamSelectModal";
import { formatSportLevel, formatRecordLevel, formatRecord, formatManner } from "./utils/teamLevel";

const SPORTS = {
  tennis:      { icon: "🎾", label: "테니스" },
  soccer:      { icon: "⚽", label: "축구" },
  futsal:      { icon: "🥅", label: "풋살" },
  badminton:   { icon: "🏸", label: "배드민턴" },
  basketball:  { icon: "🏀", label: "농구" },
  bowling:     { icon: "🎳", label: "볼링" },
  tabletennis: { icon: "🏓", label: "탁구" },
  baseball:    { icon: "⚾", label: "야구" },
  golf:        { icon: "⛳", label: "골프" },
  running:     { icon: null, label: "피클볼" },
  billiards:   { icon: "🎱", label: "당구" },
};

const WEEK_DAYS = ["일", "월", "화", "수", "목", "금", "토"];

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric" })
    + ` (${WEEK_DAYS[d.getDay()]})`;
}

function buildLocation(post) {
  return [post.location_city, post.location_district, post.location_dong, post.location_name]
    .filter(Boolean).join(" ") || "-";
}

function parseDesiredLevel(val) {
  if (!val) return "무관";
  if (Array.isArray(val)) return val.join(", ");
  try { const p = JSON.parse(val); return Array.isArray(p) ? p.join(", ") : val; } catch { return val; }
}

function formatTimeRange(start, end) {
  if (!start && !end) return null;
  if (start && end) return `${start} ~ ${end}`;
  return start || end;
}

function parseAgeGroup(ag) {
  if (!ag) return "-";
  if (Array.isArray(ag)) return ag.join(", ");
  try { const p = JSON.parse(ag); return Array.isArray(p) ? p.join(", ") : ag; } catch { return ag; }
}

function normalizeSportsType(type) {
  if (type === "tabletennis") return "table_tennis";
  if (type === "running")     return "pickleball";
  return type || "";
}

function formatSportDetail(rawType, cd) {
  if (!cd) return null;
  const t = normalizeSportsType(rawType);
  switch (t) {
    case "tennis":       return cd.ntrp ? `NTRP ${cd.ntrp}` : null;
    case "soccer":
    case "futsal":       return cd.position || null;
    case "badminton":    return cd.level || null;
    case "basketball":   return [cd.position, cd.level].filter(Boolean).join(" · ") || null;
    case "bowling":      return cd.avg ? `AVG ${cd.avg}` : null;
    case "table_tennis": return cd.level || null;
    case "baseball":     return cd.position || null;
    case "golf":         return cd.handicap ? `핸디캡 ${cd.handicap}` : null;
    case "pickleball":
    case "billiards":    return cd.years ? `구력 ${cd.years}년` : null;
    default:             return null;
  }
}

const SPORTS_LABEL_MAP = {
  tennis: "테니스", soccer: "축구", futsal: "풋살", badminton: "배드민턴",
  basketball: "농구", bowling: "볼링", table_tennis: "탁구", baseball: "야구",
  golf: "골프", pickleball: "피클볼", billiards: "당구",
};
const SPORTS_ICON_MAP = {
  tennis: "🎾", soccer: "⚽", futsal: "🥅", badminton: "🏸",
  basketball: "🏀", bowling: "🎳", table_tennis: "🏓", baseball: "⚾",
  golf: "⛳", pickleball: "🏸", billiards: "🎱",
};

export default function PostDetail({ post, onBack, onEditPost, onDeleted, onBlock }) {
  const { user, activeTeam } = useAuth();

  const [hostTeam,       setHostTeam]       = useState(null);
  const [viewerSrc,      setViewerSrc]      = useState(null);
  const [showModal,      setShowModal]      = useState(false);
  const [message,        setMessage]        = useState("");
  const [applyLoading,   setApplyLoading]   = useState(false);
  const [applyError,     setApplyError]     = useState("");
  const [applyDone,      setApplyDone]      = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading,  setDeleteLoading]  = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [blockDone, setBlockDone] = useState(false);
  const [blockError, setBlockError] = useState("");
  // 팀장 팀 목록 + 신청 시 선택된 팀
  const [myLeaderTeams, setMyLeaderTeams] = useState([]);
  const [applyTeam,     setApplyTeam]     = useState(null);
  const [showTeamSelect, setShowTeamSelect] = useState(false);
  // 이 팀의 팀원 여부 (신청 버튼 분기)
  const [isMyTeamMember, setIsMyTeamMember] = useState(false);
  // 교류전 참여 여부
  const [attending,         setAttending]         = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  // 교류전 참가자 목록
  const [attendees,         setAttendees]         = useState([]);
  const [attendeesLoading,  setAttendeesLoading]  = useState(false);
  const [showAttendeesModal, setShowAttendeesModal] = useState(false);
  // 팀 정보 모달 내 팀원 목록
  const [modalMembers,         setModalMembers]         = useState([]);
  const [modalMembersLoading,  setModalMembersLoading]  = useState(false);
  const [selectedModalMember,  setSelectedModalMember]  = useState(null);
  const [memberProfile,        setMemberProfile]        = useState(null);
  const [memberSports,         setMemberSports]         = useState([]);
  const [memberProfileLoading, setMemberProfileLoading] = useState(false);

  // 파생 변수 (useEffect dependency array에서 참조하므로 useEffect보다 먼저 선언)
  const sport       = SPORTS[post.sports_type] || { icon: "🏅", label: post.sports_type };
  const today       = new Date().toISOString().split("T")[0];
  const isConfirmed = post.status === "confirmed";
  const isExpired   = post.deadline && post.deadline < today;
  const isClosed    = post.status === "closed" || isExpired || isConfirmed;
  // 이 글 팀의 현재 팀장(teams.user_id)이 나인지 여부 — 수정/삭제 버튼 노출 기준
  const isPostLeader = !!hostTeam && hostTeam.user_id === user?.id;

  // 호스트 팀 정보 조회
  useEffect(() => {
    if (!post.team_id) return;
    supabase
      .from("teams")
      .select("*")
      .eq("id", post.team_id)
      .maybeSingle()
      .then(({ data }) => { if (data) setHostTeam(data); });
  }, [post.team_id]);

  // 내가 팀장인 팀 목록 로드
  useEffect(() => {
    if (!user?.id) return;
    supabase.from("teams").select("*").eq("user_id", user.id)
      .then(({ data }) => setMyLeaderTeams(data || []));
  }, [user?.id]);

  // 내 팀장 팀 중 하나라도 이미 신청했는지 확인
  useEffect(() => {
    if (!myLeaderTeams.length || !post.id) return;
    const ids = myLeaderTeams.map(t => t.id);
    supabase.from("applications").select("id").eq("post_id", post.id).in("guest_team_id", ids).limit(1)
      .then(({ data }) => { if (data?.length) setAlreadyApplied(true); });
  }, [myLeaderTeams.length, post.id]); // eslint-disable-line

  // 내가 이 모집글 팀의 팀원인지 확인
  useEffect(() => {
    if (!user?.id || !post.team_id) return;
    supabase.from("team_members").select("id").eq("team_id", post.team_id).eq("user_id", user.id).eq("status", "accepted").maybeSingle()
      .then(({ data }) => setIsMyTeamMember(!!data));
  }, [user?.id, post.team_id]); // eslint-disable-line

  // 교류전 참여 상태 조회 (팀장 또는 팀원일 때만)
  useEffect(() => {
    if (!user?.id || !post.id) return;
    if (!isPostLeader && !isMyTeamMember) return;
    supabase.from("attendance").select("id, status").eq("post_id", post.id).eq("user_id", user.id).maybeSingle()
      .then(({ data }) => setAttending(data?.status === "attending"));
  }, [user?.id, post.id, isPostLeader, isMyTeamMember]); // eslint-disable-line

  // 교류전 참가자 목록 조회 (attending 변경 시 실시간 갱신)
  useEffect(() => {
    if (!post.id) return;
    setAttendeesLoading(true);
    supabase.from("attendance").select("user_id").eq("post_id", post.id).eq("status", "attending")
      .then(async ({ data: rows }) => {
        if (!rows || rows.length === 0) { setAttendees([]); setAttendeesLoading(false); return; }
        const userIds    = rows.map(r => r.user_id);
        const normalType = normalizeSportsType(post.sports_type);
        const [{ data: profiles }, { data: sports }] = await Promise.all([
          supabase.from("user_profiles").select("id, nickname, profile_image, gender, is_profile_public").in("id", userIds),
          supabase.from("user_sports").select("user_id, career_detail, is_player").in("user_id", userIds).eq("sports_type", normalType),
        ]);
        const profileMap = Object.fromEntries((profiles || []).map(p => [p.id, p]));
        const sportMap   = Object.fromEntries((sports   || []).map(sp => [sp.user_id, sp]));
        setAttendees(rows.map(r => ({
          user_id:   r.user_id,
          team_nick: null,
          profile:   profileMap[r.user_id] || null,
          sport:     sportMap[r.user_id]   || null,
        })));
        setAttendeesLoading(false);
      });
  }, [post.id, attending]); // eslint-disable-line

  // 팀 정보 모달 열릴 때 팀원 목록 로드
  useEffect(() => {
    if (!showTeamModal || !post.team_id || !hostTeam?.sports_type) return;
    setModalMembersLoading(true);
    supabase.from("team_members").select("id, user_id, nickname").eq("team_id", post.team_id).eq("status", "accepted")
      .then(async ({ data: rows }) => {
        if (!rows || rows.length === 0) { setModalMembers([]); setModalMembersLoading(false); return; }
        const userIds    = rows.map(r => r.user_id);
        const normalType = normalizeSportsType(hostTeam.sports_type);
        const [{ data: profiles }, { data: sports }] = await Promise.all([
          supabase.from("user_profiles").select("id, nickname, profile_image, gender").in("id", userIds),
          supabase.from("user_sports").select("user_id, career_detail, is_player").in("user_id", userIds).eq("sports_type", normalType),
        ]);
        const profileMap = Object.fromEntries((profiles || []).map(p => [p.id, p]));
        const sportMap   = Object.fromEntries((sports   || []).map(sp => [sp.user_id, sp]));
        setModalMembers(rows.map(r => ({
          id: r.id, user_id: r.user_id,
          team_nick: r.nickname || null,
          profile: profileMap[r.user_id] || null,
          sport:   sportMap[r.user_id]   || null,
        })));
        setModalMembersLoading(false);
      });
  }, [showTeamModal, post.team_id, hostTeam?.sports_type]); // eslint-disable-line

  // 팀원 카드 클릭 시 상세 프로필 로드
  useEffect(() => {
    if (!selectedModalMember) return;
    setMemberProfileLoading(true);
    const normalType = normalizeSportsType(hostTeam?.sports_type || "");
    Promise.all([
      supabase.from("user_profiles").select("*").eq("id", selectedModalMember.user_id).maybeSingle(),
      supabase.from("user_sports").select("*").eq("user_id", selectedModalMember.user_id).eq("sports_type", normalType),
    ]).then(([{ data: fp }, { data: sp }]) => {
      setMemberProfile(fp || selectedModalMember.profile || null);
      setMemberSports(sp || []);
      setMemberProfileLoading(false);
    });
  }, [selectedModalMember]); // eslint-disable-line

  const closeTeamModal = () => { setShowTeamModal(false); setSelectedModalMember(null); };

  const handleAttend = async () => {
    if (attendanceLoading) return;
    setAttendanceLoading(true);
    if (attending) {
      await supabase.from("attendance").delete().eq("post_id", post.id).eq("user_id", user.id);
      setAttending(false);
    } else {
      await supabase.from("attendance").upsert([{
        post_id: post.id,
        team_id: post.team_id,
        user_id: user.id,
        status: "attending",
      }], { onConflict: "post_id,user_id" });
      setAttending(true);
    }
    setAttendanceLoading(false);
  };

  const handleDeletePost = async () => {
    setDeleteLoading(true);
    // 1. 해당 모집글에 달린 신청 내역 전체 삭제
    await supabase.from("applications").delete().eq("post_id", post.id);
    // 2. 모집글 삭제
    await supabase.from("posts").delete().eq("id", post.id);
    setDeleteLoading(false);
    // 3. 목록으로 이동
    onDeleted?.();
  };

  const handleBlock = async () => {
    setBlockError("");
    if (!activeTeam?.id) {
      setBlockError("활성 팀을 먼저 설정해주세요.");
      return;
    }
    if (!hostTeam?.id) return;
    setBlockLoading(true);
    const { error } = await supabase.from("blocks").insert({
      blocker_team_id: activeTeam.id,
      blocked_team_id: hostTeam.id,
    });
    setBlockLoading(false);
    if (error && !error.message?.includes("duplicate")) {
      setBlockError("차단 처리 중 오류가 발생했어요: " + error.message);
      return;
    }
    setBlockDone(true);
    onBlock?.();
  };

  // 신청 버튼 클릭 → 팀장 팀 수 분기
  const openModal = () => {
    if (!user?.id) return;
    if (myLeaderTeams.length === 0) {
      alert("교류전 신청은 팀장만 할 수 있어요. 팀을 먼저 만들어주세요.");
      return;
    }
    if (myLeaderTeams.length === 1) {
      setApplyTeam(myLeaderTeams[0]);
      setMessage(""); setApplyError(""); setApplyDone(false);
      setShowModal(true);
    } else {
      setShowTeamSelect(true);
    }
  };

  const handleTeamSelected = (team) => {
    setApplyTeam(team);
    setShowTeamSelect(false);
    setMessage(""); setApplyError(""); setApplyDone(false);
    setShowModal(true);
  };

  const submitApply = async () => {
    if (!applyTeam?.id) { setApplyError("팀을 선택해주세요"); return; }
    if (applyTeam.id === post.team_id) { setApplyError("내 팀 모집글에는 신청할 수 없어요"); return; }
    if (applyTeam.sports_type !== post.sports_type) {
      setApplyError(`종목이 일치하지 않아요 (내 팀: ${SPORTS[applyTeam.sports_type]?.label || applyTeam.sports_type})`);
      return;
    }
    setApplyLoading(true);
    setApplyError("");

    // 중복 신청 체크
    const { data: existing } = await supabase
      .from("applications")
      .select("id")
      .eq("post_id", post.id)
      .eq("guest_team_id", applyTeam.id)
      .maybeSingle();

    if (existing) {
      setApplyError("이미 이 모집글에 신청했어요");
      setApplyLoading(false);
      return;
    }

    const { data: appData, error } = await supabase
      .from("applications")
      .insert([{
        host_team_id:  post.team_id,
        guest_team_id: applyTeam.id,
        post_id:       post.id,
        message:       message.trim() || null,
        status:        "pending",
      }])
      .select().single();

    if (error) {
      setApplyError("신청 중 오류가 발생했어요: " + error.message);
      setApplyLoading(false);
      return;
    }

    await supabase.from("notifications").insert([{
      team_id:    post.team_id,
      type:       "match_request",
      title:      "⚡ 새 매칭 신청이 왔어요!",
      body:       `${applyTeam.team_name} 팀이 모집글에 신청했어요`,
      related_id: appData?.id || null,
    }]);

    setApplyLoading(false);
    setApplyDone(true);
    setAlreadyApplied(true);
  };

  return (
    <div style={s.container}>
      {/* 헤더 */}
      <div style={s.header}>
        <button style={s.backBtn} onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="#E24B4A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          뒤로
        </button>
        <span style={s.headerTitle}>모집글 상세</span>
        {isPostLeader ? (
          <div style={{ display: "flex", gap: 6 }}>
            <button style={s.headerActionBtn} onClick={() => onEditPost?.(post)}>수정</button>
            <button style={{ ...s.headerActionBtn, color: "#E24B4A", borderColor: "#fecaca" }} onClick={() => setShowDeleteConfirm(true)}>삭제</button>
          </div>
        ) : (
          <div style={{ position: "relative" }}>
            <button style={s.menuBtn} onClick={() => setShowMenu((v) => !v)}>⋯</button>
            {showMenu && (
              <div style={s.menuDropdown}>
                <button style={s.menuDropdownItem} onClick={() => { setShowMenu(false); setShowReport(true); }}>
                  🚨 신고하기
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={s.body}>
        {/* 교류전 확정 배너 */}
        {isConfirmed && (
          <div style={{ padding: "12px 16px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>✅</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#16a34a" }}>교류전이 확정된 모집글입니다</div>
              <div style={{ fontSize: 12, color: "#4ade80", marginTop: 2 }}>새로운 신청을 받지 않습니다</div>
            </div>
          </div>
        )}

        {/* 히어로 카드 */}
        <div style={s.heroCard}>
          {/* 팀 사진: logo_url → team_photo → 종목 이모지 */}
          {(hostTeam?.logo_url || hostTeam?.team_photo) ? (
            <img
              src={hostTeam.logo_url || hostTeam.team_photo}
              alt="팀 사진"
              loading="lazy"
              onClick={() => setViewerSrc(hostTeam.logo_url || hostTeam.team_photo)}
              style={{ width: 64, height: 64, borderRadius: 14, objectFit: "cover", border: "1px solid #e5e7eb", flexShrink: 0, cursor: "pointer" }}
            />
          ) : (
            <div style={{ width: 64, height: 64, borderRadius: 14, background: "#f5f6fa", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {sport.icon
                ? <span style={{ fontSize: 30 }}>{sport.icon}</span>
                : <img src={pickleballIcon} alt="피클볼" style={{ width: 32, height: 32, objectFit: "contain" }} />}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={s.teamName}>{post.team_name}</div>
            <div style={s.sportLabel}>{sport.label}</div>
            {isClosed && (
              <div style={{ marginTop: 6, display: "inline-block", padding: "2px 10px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 999, fontSize: 11, color: "#9ca3af", fontWeight: 700 }}>
                {isConfirmed ? "교류전 확정됨" : post.status === "closed" ? "모집 마감" : "기간 만료"}
              </div>
            )}
          </div>
        </div>

        {/* 상세 정보 */}
        <div style={{ ...s.infoCard, border: "1.5px solid #E24B4A" }}>
          <InfoRow label="교류전 날짜"    value={formatDate(post.game_date)} highlight />
          {formatTimeRange(post.game_time_start, post.game_time_end) && (
            <InfoRow label="교류전 시간"  value={formatTimeRange(post.game_time_start, post.game_time_end)} highlight />
          )}
          <LocationRow post={post} />
          <InfoRow label="희망 상대 레벨" value={parseDesiredLevel(post.desired_level)} />
          {(post.participant_male != null || post.participant_female != null) && (
            <InfoRow
              label="희망 참가 인원"
              value={`남 ${post.participant_male ?? 0}명 / 여 ${post.participant_female ?? 0}명`}
            />
          )}
          {post.deadline && (
            <InfoRow label="모집 마감일" value={formatDate(post.deadline)} />
          )}
        </div>

        {/* 모집 메시지 */}
        {post.message && (
          <div style={{ ...s.msgCard, border: "1.5px solid #edf0f3" }}>
            <div style={s.msgTitle}>모집 메시지</div>
            <div style={s.msgText}>{post.message}</div>
          </div>
        )}

        {/* 교류전 참가 인원 — 컴팩트 요약 */}
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", padding: "14px 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: attendeesLoading || attendees.length > 0 ? 12 : 0 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#1A1A2E" }}>
              {sport.icon || "🏅"} 이번 교류전 참가 인원 ({attendees.length}명)
            </span>
            {attendees.length > 0 && (
              <button
                style={{ fontSize: 13, color: "#E8412A", fontWeight: 700, background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit" }}
                onClick={() => setShowAttendeesModal(true)}
              >
                전체보기 {">"}
              </button>
            )}
          </div>
          <style>{`@keyframes pdSpin{to{transform:rotate(360deg)}}`}</style>
          {attendeesLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
              <div style={{ width: 20, height: 20, borderRadius: "50%", border: "3px solid #f0f0f0", borderTop: "3px solid #E8412A", animation: "pdSpin 0.8s linear infinite" }} />
            </div>
          ) : attendees.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>아직 참가 신청한 팀원이 없어요</p>
          ) : (
            // 최대 5명 아바타 미리보기 + 초과 시 +N
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {attendees.slice(0, 5).map((a, i) => {
                const nick = a.profile?.nickname || "?";
                const initial = nick[0]?.toUpperCase() || "?";
                return a.profile?.profile_image ? (
                  <img key={a.user_id} src={a.profile.profile_image} alt={nick}
                    style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", border: "2px solid #fff", marginLeft: i > 0 ? -8 : 0, flexShrink: 0 }} />
                ) : (
                  <div key={a.user_id}
                    style={{ width: 32, height: 32, borderRadius: "50%", background: "#E8412A", display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #fff", marginLeft: i > 0 ? -8 : 0, flexShrink: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{initial}</span>
                  </div>
                );
              })}
              {attendees.length > 5 && (
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #fff", marginLeft: -8, flexShrink: 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280" }}>+{attendees.length - 5}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 참가 신청 팀원 (공개 동의) */}
        {(() => {
          const publicAttendees = attendees.filter(a => a.profile?.is_profile_public === true);
          if (publicAttendees.length === 0) return null;
          return (
            <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", padding: "14px 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1A1A2E", marginBottom: 12 }}>
                참가 신청 팀원
              </div>
              {publicAttendees.map(a => {
                const nick    = a.profile?.nickname || "?";
                const initial = nick[0]?.toUpperCase() || "?";
                const level   = formatSportDetail(post.sports_type, a.sport?.career_detail);
                return (
                  <div key={a.user_id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#1A1A2E", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{initial}</span>
                    </div>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#1A1A2E" }}>{nick}</span>
                      {level && <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 6 }}>{level}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* 호스트 팀 정보 */}
        {hostTeam && (
          <>
            <div style={s.sectionLabel}>호스트 팀 정보</div>

            {/* 팀 로고 + 이름 */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
              {hostTeam.logo_url ? (
                <img src={hostTeam.logo_url} alt="팀 로고" loading="lazy" style={s.hostLogo} onClick={() => setViewerSrc(hostTeam.logo_url)} />
              ) : (
                <div style={s.hostLogoFallback}>{SPORTS[hostTeam.sports_type]?.icon || "🏅"}</div>
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>{hostTeam.team_name}</div>
                <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{SPORTS[hostTeam.sports_type]?.label || hostTeam.sports_type}</div>
              </div>
              <button style={s.viewTeamBtn} onClick={() => { setBlockDone(false); setBlockError(""); setShowTeamModal(true); }}>
                팀 정보 보기
              </button>
            </div>

            {/* 단체사진 */}
            {hostTeam.team_photo && (
              <img src={hostTeam.team_photo} alt="단체사진" loading="lazy" style={s.teamPhoto} onClick={() => setViewerSrc(hostTeam.team_photo)} />
            )}

            {/* 팀 소개 */}
            <div style={s.msgCard}>
              <div style={s.msgTitle}>팀 소개</div>
              <div style={s.msgText}>{hostTeam.intro || "아직 팀 소개글이 없어요."}</div>
            </div>

            {/* 팀 상세 */}
            <div style={s.infoCard}>
              <InfoRow label="구력 레벨" value={formatSportLevel(hostTeam.sports_type, hostTeam.level)} />
              {hostTeam.skill_level && <InfoRow label="실력" value={hostTeam.skill_level} />}
              <InfoRow label="전적 레벨" value={formatRecordLevel(hostTeam)} />
              <InfoRow label="전적" value={formatRecord(hostTeam)} />
              <InfoRow label="매너 점수" value={formatManner(hostTeam)} />
              <InfoRow
                label="팀 인원"
                value={
                  (hostTeam.male_count || 0) + (hostTeam.female_count || 0) > 0
                    ? `${(hostTeam.male_count || 0) + (hostTeam.female_count || 0)}명 (남 ${hostTeam.male_count || 0} / 여 ${hostTeam.female_count || 0})`
                    : "-"
                }
              />
              <InfoRow label="연령대"    value={parseAgeGroup(hostTeam.age_group)} />
              <InfoRow label="평균 구력" value={hostTeam.member_avg_career || "-"} />
              {hostTeam.region && (
                <InfoRow label="주요 활동 지역" value={`서울 ${hostTeam.region}`} />
              )}
              {hostTeam.main_place && (
                <InfoRow label="주 활동 장소" value={hostTeam.main_place} />
              )}
              <InfoRow
                label="주차"
                value={
                  hostTeam.parking == null ? "-"
                  : hostTeam.parking
                    ? `가능${hostTeam.parking_count ? ` (${hostTeam.parking_count}대)` : ""}`
                    : "불가능"
                }
              />
            </div>
          </>
        )}
      </div>

      {/* 하단 버튼 영역 */}
      <div style={s.footer}>
        {(isPostLeader || isMyTeamMember) ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "stretch" }}>
            {attending && (
              <div style={{ textAlign: "center", fontSize: 13, color: "#16a34a", fontWeight: 600 }}>참여 중이에요 ✓</div>
            )}
            <button
              style={{ ...s.applyBtn, background: attending ? "#9ca3af" : "#E8412A", cursor: attendanceLoading ? "default" : "pointer", opacity: attendanceLoading ? 0.6 : 1 }}
              onClick={handleAttend}
              disabled={attendanceLoading}
            >
              {attendanceLoading ? "처리 중..." : attending ? "참여 취소하기" : "교류전 참여하기"}
            </button>
          </div>
        ) : !user?.id ? (
          <div style={{ textAlign: "center", fontSize: 13, color: "#9ca3af", padding: "4px 0" }}>
            로그인 후 신청할 수 있어요
          </div>
        ) : (
          <button
            style={{ ...s.applyBtn, opacity: (isClosed || alreadyApplied) ? 0.5 : 1, background: (isClosed || alreadyApplied) ? "#9ca3af" : "#E24B4A", cursor: (isClosed || alreadyApplied) ? "default" : "pointer" }}
            onClick={(isClosed || alreadyApplied) ? undefined : openModal}
            disabled={isClosed || alreadyApplied}
          >
            {isConfirmed ? "교류전이 확정됐어요" : alreadyApplied ? "이미 신청하였습니다" : isClosed ? "모집이 마감됐어요" : "⚡ 신청하기"}
          </button>
        )}
      </div>

      {/* 참가 인원 전체보기 모달 */}
      {showAttendeesModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 260, padding: "0 20px" }}
          onClick={() => setShowAttendeesModal(false)}>
          <div style={{ background: "#fff", borderRadius: 18, width: "100%", maxWidth: 400, maxHeight: "70vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 32px rgba(0,0,0,0.20)" }}
            onClick={e => e.stopPropagation()}>
            {/* 모달 헤더 */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 20px 14px", borderBottom: "1px solid #f3f4f6", flexShrink: 0 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: "#1A1A2E" }}>교류전 참가 인원 ({attendees.length}명)</span>
              <button style={{ background: "none", border: "none", fontSize: 18, color: "#6b7280", cursor: "pointer", lineHeight: 1, padding: 4 }} onClick={() => setShowAttendeesModal(false)}>✕</button>
            </div>
            {/* 참가자 목록 (스크롤) */}
            <div style={{ overflowY: "auto", padding: "12px 16px 20px" }}>
              {attendees.map(a => (
                <ModalMemberCard
                  key={a.user_id}
                  member={a}
                  sportsType={post.sports_type}
                  hostTeam={hostTeam}
                  onClick={() => { setShowAttendeesModal(false); setSelectedModalMember(a); }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteConfirm && (
        <div style={s.backdrop} onClick={() => setShowDeleteConfirm(false)}>
          <div style={s.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 22 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🗑️</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 8 }}>모집글을 삭제하시겠어요?</div>
              <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.7 }}>
                삭제하면 해당 모집글에 신청한 내용도<br />모두 사라집니다.
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button style={s.cancelBtn} onClick={() => setShowDeleteConfirm(false)} disabled={deleteLoading}>취소</button>
              <button
                style={{ ...s.primaryBtn, flex: 1, background: "#E24B4A", opacity: deleteLoading ? 0.6 : 1 }}
                onClick={handleDeletePost}
                disabled={deleteLoading}
              >
                {deleteLoading ? "삭제 중..." : "삭제하기"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 신청 모달 */}
      {viewerSrc && <ImageViewer src={viewerSrc} onClose={() => setViewerSrc(null)} />}

      {/* 팀 정보 보기 모달 */}
      {showTeamModal && hostTeam && (
        <div style={s.backdrop} onClick={closeTeamModal}>
          <div style={s.teamModalBox} onClick={(e) => e.stopPropagation()}>
            <style>{`@keyframes pdSpin{to{transform:rotate(360deg)}}`}</style>
            {blockDone ? (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <div style={{ fontSize: 44, marginBottom: 12 }}>🚫</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 8 }}>차단 완료</div>
                <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.7, marginBottom: 24 }}>
                  해당 팀의 모집글이<br />더 이상 노출되지 않습니다.
                </div>
                <button style={s.primaryBtn} onClick={() => { closeTeamModal(); setBlockDone(false); }}>확인</button>
              </div>
            ) : (
              <>
                {/* 팀 헤더 */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, paddingBottom: 16, borderBottom: "1px solid #f3f4f6" }}>
                  {hostTeam.logo_url ? (
                    <img src={hostTeam.logo_url} alt="팀 로고" loading="lazy" style={{ width: 52, height: 52, borderRadius: 12, objectFit: "cover", border: "1px solid #e5e7eb" }} />
                  ) : (
                    <div style={{ width: 52, height: 52, borderRadius: 12, background: "#f5f6fa", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>
                      {SPORTS[hostTeam.sports_type]?.icon || "🏅"}
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>{hostTeam.team_name}</div>
                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{SPORTS[hostTeam.sports_type]?.label || hostTeam.sports_type}</div>
                  </div>
                </div>

                {/* 단체사진 */}
                {hostTeam.team_photo && (
                  <img src={hostTeam.team_photo} alt="단체사진" loading="lazy" style={{ width: "100%", borderRadius: 12, marginBottom: 14, maxHeight: 160, objectFit: "cover", display: "block" }} />
                )}

                {/* 팀 소개 */}
                {hostTeam.intro && (
                  <div style={{ marginBottom: 14, padding: "12px 14px", background: "#f5f6fa", borderRadius: 10, fontSize: 13, color: "#6b7280", lineHeight: 1.6 }}>
                    {hostTeam.intro}
                  </div>
                )}

                {/* 팀 상세 */}
                <div style={{ background: "#f9fafb", borderRadius: 12, padding: "4px 14px", marginBottom: 16, border: "1px solid #f3f4f6" }}>
                  <InfoRow label="구력 레벨" value={formatSportLevel(hostTeam.sports_type, hostTeam.level)} />
                  {hostTeam.skill_level && <InfoRow label="실력" value={hostTeam.skill_level} />}
                  <InfoRow label="전적 레벨" value={formatRecordLevel(hostTeam)} />
                  <InfoRow label="전적" value={formatRecord(hostTeam)} />
                  <InfoRow label="매너 점수" value={formatManner(hostTeam)} />
                  <InfoRow label="팀 인원" value={
                    (hostTeam.male_count || 0) + (hostTeam.female_count || 0) > 0
                      ? `${(hostTeam.male_count || 0) + (hostTeam.female_count || 0)}명 (남 ${hostTeam.male_count || 0} / 여 ${hostTeam.female_count || 0})`
                      : "-"
                  } />
                  <InfoRow label="연령대"    value={parseAgeGroup(hostTeam.age_group)} />
                  <InfoRow label="평균 구력" value={hostTeam.member_avg_career || "-"} />
                </div>

                {/* 팀원 목록 */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 8 }}>팀원 ({modalMembers.length}명)</div>
                  {modalMembersLoading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: "14px 0" }}>
                      <div style={{ width: 22, height: 22, borderRadius: "50%", border: "3px solid #f0f0f0", borderTop: "3px solid #E8412A", animation: "pdSpin 0.8s linear infinite" }} />
                    </div>
                  ) : modalMembers.length === 0 ? (
                    <p style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", padding: "12px 0", margin: 0 }}>아직 팀원이 없어요</p>
                  ) : (
                    modalMembers.map(m => (
                      <ModalMemberCard
                        key={m.id}
                        member={m}
                        sportsType={hostTeam.sports_type}
                        hostTeam={hostTeam}
                        onClick={() => setSelectedModalMember(m)}
                      />
                    ))
                  )}
                </div>

                {/* 신고 / 차단 버튼 */}
                {blockError && (
                  <div style={{ marginBottom: 10, padding: "10px 12px", background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 8, fontSize: 13, color: "#E24B4A" }}>
                    {blockError}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <button style={s.reportActionBtn} onClick={() => { closeTeamModal(); setShowReport(true); }}>
                    🚨 신고하기
                  </button>
                  <button
                    style={{ ...s.blockActionBtn, opacity: blockLoading ? 0.6 : 1, cursor: blockLoading ? "default" : "pointer" }}
                    onClick={handleBlock}
                    disabled={blockLoading}
                  >
                    🚫 {blockLoading ? "처리 중..." : "차단하기"}
                  </button>
                </div>

                <button style={{ ...s.cancelBtn, width: "100%", textAlign: "center" }} onClick={closeTeamModal}>닫기</button>
              </>
            )}
          </div>
        </div>
      )}

      {selectedModalMember && (
        <ModalMemberProfile
          member={selectedModalMember}
          profile={memberProfile}
          sports={memberSports}
          loading={memberProfileLoading}
          sportsType={hostTeam?.sports_type}
          onClose={() => setSelectedModalMember(null)}
        />
      )}

      {showReport && (
        <ReportModal
          targetType="post"
          targetId={post.id}
          targetName={post.team_name}
          onClose={() => setShowReport(false)}
        />
      )}

      {showTeamSelect && (
        <TeamSelectModal
          teams={myLeaderTeams}
          onSelect={handleTeamSelected}
          onClose={() => setShowTeamSelect(false)}
        />
      )}

      {showModal && (
        <div style={s.backdrop} onClick={() => setShowModal(false)}>
          <div style={s.modalBox} onClick={(e) => e.stopPropagation()}>
            {applyDone ? (
              <div style={{ textAlign: "center", padding: "8px 0" }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 8 }}>신청 완료!</div>
                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>
                  <span style={{ color: "#E24B4A", fontWeight: 700 }}>{post.team_name}</span>의 모집글에 신청했어요
                </div>
                <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 24 }}>
                  '내 매칭' 탭에서 결과를 확인하세요
                </div>
                <button style={s.primaryBtn} onClick={() => setShowModal(false)}>확인</button>
              </div>
            ) : (
              <>
                <div style={{ textAlign: "center", marginBottom: 20 }}>
                  <div style={{ fontSize: 20, marginBottom: 6 }}>⚡</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 4 }}>매칭 신청</div>
                  <div style={{ fontSize: 13, color: "#6b7280" }}>
                    <span style={{ color: "#E24B4A", fontWeight: 700 }}>{post.team_name}</span>의 모집글에 신청합니다
                  </div>
                </div>

                {/* 신청 팀 */}
                <div style={{ marginBottom: 16, padding: "12px 14px", background: "#f5f6fa", borderRadius: 10, border: "1px solid #e5e7eb" }}>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>신청 팀</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>🏅 {applyTeam?.team_name}</div>
                </div>

                {/* 교류전 일정 미리보기 */}
                <div style={{ marginBottom: 16, padding: "10px 14px", background: "#fff8f8", borderRadius: 10, border: "1px solid #fecaca", fontSize: 12, color: "#374151" }}>
                  <span style={{ color: "#E24B4A", fontWeight: 700 }}>📅 {formatDate(post.game_date)}{formatTimeRange(post.game_time_start, post.game_time_end) ? ` ${formatTimeRange(post.game_time_start, post.game_time_end)}` : ""}</span>
                  {buildLocation(post) !== "-" && (
                    <span style={{ color: "#6b7280", marginLeft: 8 }}>📍 {buildLocation(post)}</span>
                  )}
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
                    신청 메시지 <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400, marginLeft: 6 }}>선택</span>
                  </div>
                  <textarea
                    style={{ width: "100%", padding: "13px 14px", background: "#f5f6fa", border: "1px solid #e5e7eb", borderRadius: 10, color: "#111827", fontSize: 14, outline: "none", resize: "none", lineHeight: 1.6, fontFamily: "inherit", boxSizing: "border-box" }}
                    rows={3}
                    placeholder="간단한 인사말이나 참여 의사를 남겨보세요"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={200}
                  />
                </div>

                {applyError && <p style={{ fontSize: 12, color: "#E24B4A", marginBottom: 12 }}>{applyError}</p>}

                <div style={{ display: "flex", gap: 10 }}>
                  <button style={s.cancelBtn} onClick={() => setShowModal(false)} disabled={applyLoading}>취소</button>
                  <button
                    style={{ ...s.primaryBtn, flex: 1, opacity: applyLoading ? 0.6 : 1 }}
                    onClick={submitApply}
                    disabled={applyLoading}
                  >
                    {applyLoading ? "신청 중..." : "신청하기"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, highlight }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #f0f1f5" }}>
      <span style={{ fontSize: 13, color: "#9ca3af", flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: highlight ? "#E24B4A" : "#374151", fontWeight: highlight ? 700 : 600, textAlign: "right", marginLeft: 8 }}>{value}</span>
    </div>
  );
}

function LocationRow({ post }) {
  const [debugMsg, setDebugMsg] = useState("");

  const fullLocation = [post.location_city, post.location_district, post.location_dong, post.location_name]
    .filter(Boolean).join(" ") || "-";

  const hasLocation = fullLocation !== "-";
  const hasExactLocation = !!(post.location_mapx && post.location_mapy);
  const lng = hasExactLocation ? Number(post.location_mapx) / 10000000 : null;
  const lat = hasExactLocation ? Number(post.location_mapy) / 10000000 : null;
  const placeName = post.location_name || post.location_road_address || fullLocation;

  // 웹 fallback URL
  const webMapUrl = hasExactLocation
    ? `https://map.naver.com/p/?c=${lng},${lat},17,0,0,0,dh`
    : `https://map.naver.com/p/search/${encodeURIComponent(
        post.location_name
          ? [post.location_name, post.location_district, post.location_dong].filter(Boolean).join(" ")
          : fullLocation
      )}`;

  const showDebug = (msg) => {
    console.log("[MapBtn]", msg);
    setDebugMsg(msg);
    setTimeout(() => setDebugMsg(""), 4000);
  };

  const handleOpenMap = async () => {
    if (!hasLocation) {
      showDebug("ERR: no location");
      return;
    }

    // Capacitor 네이티브 환경 감지
    let isNative = false;
    try {
      const { Capacitor } = await import("@capacitor/core");
      isNative = Capacitor.isNativePlatform();
    } catch (_) {
      isNative = false;
    }

    const ua = navigator.userAgent || "";
    const isAndroid = /android/i.test(ua);

    showDebug(
      `native=${isNative} android=${isAndroid} exact=${hasExactLocation} x=${post.location_mapx} y=${post.location_mapy}`
    );

    try {
      // 네이티브 앱(Capacitor) + 좌표 있음 → AppLauncher로 nmap:// 앱 열기
      if (isNative && hasExactLocation) {
        const nmapUrl = `nmap://place?lat=${lat}&lng=${lng}&name=${encodeURIComponent(placeName)}&appname=com.crewpick.android`;
        showDebug(`AppLauncher: ${nmapUrl.slice(0, 80)}`);

        const { AppLauncher } = await import("@capacitor/app-launcher");
        const canOpen = await AppLauncher.canOpenUrl({ url: nmapUrl });
        showDebug(`canOpen=${canOpen.value}`);

        if (canOpen.value) {
          await AppLauncher.openUrl({ url: nmapUrl });
          return;
        }

        // 앱 미설치 → Capacitor Browser로 웹 지도 열기
        showDebug(`app not found, opening web`);
        try {
          const { Browser } = await import("@capacitor/browser");
          await Browser.open({ url: webMapUrl });
        } catch (e) {
          showDebug(`Browser err: ${e.message}`);
          window.location.href = webMapUrl;
        }
        return;
      }

      // 브라우저 환경: Android 모바일 브라우저에서는 intent:// 시도, 그 외 웹 지도
      if (!isNative && isAndroid && hasExactLocation) {
        const fallback = encodeURIComponent(webMapUrl);
        const intentUrl =
          `intent://place?lat=${lat}&lng=${lng}&name=${encodeURIComponent(placeName)}&appname=com.crewpick.android` +
          `#Intent;scheme=nmap;package=com.nhn.android.nmap;S.browser_fallback_url=${fallback};end`;
        showDebug(`intent: ${intentUrl.slice(0, 80)}`);
        window.location.href = intentUrl;
        return;
      }

      // iOS / 데스크톱 / 좌표 없음 → 일반 웹 지도
      showDebug(`web: ${webMapUrl.slice(0, 80)}`);
      window.open(webMapUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      showDebug(`FATAL: ${err.message}`);
    }
  };

  return (
    <div style={{ padding: "12px 0", borderBottom: "1px solid #f0f1f5" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <span style={{ fontSize: 13, color: "#9ca3af", flexShrink: 0, paddingTop: 2 }}>장소</span>
        <span style={{ fontSize: 13, color: "#374151", fontWeight: 600, textAlign: "right", lineHeight: 1.5, wordBreak: "keep-all" }}>
          {fullLocation}
        </span>
      </div>
      {post.location_road_address && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
          <span style={{ fontSize: 12, color: "#9ca3af", textAlign: "right", lineHeight: 1.4 }}>
            📍 {post.location_road_address}
          </span>
        </div>
      )}
      {hasLocation && (
        <button
          type="button"
          onClick={handleOpenMap}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 6, width: "100%", minHeight: 48, marginTop: 12,
            padding: "12px 16px", background: "#fff5f5",
            border: "1.5px solid #E24B4A", borderRadius: 12,
            fontSize: 14, fontWeight: 700, color: "#E24B4A",
            cursor: "pointer", fontFamily: "inherit",
            WebkitTapHighlightColor: "rgba(226,75,74,0.15)",
            touchAction: "manipulation",
            position: "relative", zIndex: 1,
          }}
        >
          🗺️ {hasExactLocation ? "지도에서 정확한 위치 보기" : "네이버 지도에서 보기"}
        </button>
      )}
      {debugMsg && (
        <div style={{
          marginTop: 8, padding: "8px 12px",
          background: "#1f2937", color: "#e5e7eb",
          borderRadius: 8, fontSize: 11,
          fontFamily: "monospace", wordBreak: "break-all",
          lineHeight: 1.4,
        }}>
          🐛 {debugMsg}
        </div>
      )}
    </div>
  );
}

const s = {
  hostLogo:        { width: 56, height: 56, borderRadius: 12, objectFit: "cover", border: "1px solid #e5e7eb", cursor: "pointer", flexShrink: 0 },
  hostLogoFallback:{ width: 56, height: 56, borderRadius: 12, background: "#f5f6fa", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 },
  teamPhoto:       { width: "100%", height: 180, objectFit: "cover", borderRadius: 14, border: "1px solid #e5e7eb", cursor: "pointer", display: "block" },
  container:   { minHeight: "calc(100dvh - 72px)", background: "#f5f6fa", display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto", fontFamily: "'Noto Sans KR', sans-serif" },
  header:      { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "calc(env(safe-area-inset-top) + 12px) 20px 12px", background: "#fff", borderBottom: "1px solid #e5e7eb", position: "sticky", top: 0, zIndex: 10, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" },
  backBtn:     { display: "flex", alignItems: "center", gap: 2, background: "none", border: "none", fontSize: 14, fontWeight: 600, color: "#E24B4A", cursor: "pointer", padding: "4px 0" },
  headerTitle: { fontSize: 16, fontWeight: 700, color: "#111827" },
  body:        { flex: 1, padding: "20px 20px calc(160px + env(safe-area-inset-bottom))", display: "flex", flexDirection: "column", gap: 14 },
  heroCard:    { background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", padding: "14px 16px", display: "flex", flexDirection: "row", alignItems: "center", gap: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" },
  teamName:    { fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 3 },
  sportLabel:  { fontSize: 12, color: "#9ca3af" },
  infoCard:    { background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", padding: "4px 16px", boxShadow: "0 1px 6px rgba(0,0,0,0.04)" },
  msgCard:     { background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", padding: "16px", boxShadow: "0 1px 6px rgba(0,0,0,0.04)" },
  msgTitle:    { fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 8 },
  msgText:     { fontSize: 14, color: "#6b7280", lineHeight: 1.7 },
  sectionLabel:{ fontSize: 13, fontWeight: 700, color: "#374151", paddingLeft: 2 },
  footer:      { position: "fixed", bottom: "calc(72px + env(safe-area-inset-bottom))", left: 0, right: 0, maxWidth: 430, margin: "0 auto", padding: "12px 20px 16px", borderTop: "1px solid #e5e7eb", background: "#fff", zIndex: 50, boxShadow: "0 -2px 8px rgba(0,0,0,0.06)" },
  applyBtn:        { width: "100%", padding: "14px", color: "#fff", border: "none", borderRadius: 12, fontSize: 16, fontWeight: 700 },
  headerActionBtn: { padding: "5px 10px", background: "#f5f6fa", color: "#374151", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  menuBtn:         { padding: "4px 10px", background: "none", border: "none", fontSize: 20, color: "#6b7280", cursor: "pointer", lineHeight: 1 },
  menuDropdown:    { position: "absolute", top: "100%", right: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", minWidth: 130, zIndex: 50, overflow: "hidden" },
  menuDropdownItem:{ width: "100%", padding: "12px 16px", background: "none", border: "none", fontSize: 14, fontWeight: 600, color: "#374151", textAlign: "left", cursor: "pointer", fontFamily: "inherit" },
  backdrop:    { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, backdropFilter: "blur(4px)" },
  modalBox:    { width: "90%", maxWidth: 360, background: "#fff", borderRadius: 18, padding: "22px 20px 26px", boxShadow: "0 8px 32px rgba(0,0,0,0.20)", maxHeight: "85vh", overflowY: "auto" },
  primaryBtn:  { padding: "13px 0", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer", width: "100%" },
  cancelBtn:       { padding: "13px 20px", background: "#f5f6fa", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer" },
  viewTeamBtn:     { padding: "7px 12px", background: "#f5f6fa", color: "#374151", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", flexShrink: 0, fontFamily: "inherit" },
  teamModalBox:    { width: "92%", maxWidth: 380, background: "#fff", borderRadius: 20, padding: "22px 20px 24px", boxShadow: "0 8px 32px rgba(0,0,0,0.20)", maxHeight: "88vh", overflowY: "auto" },
  reportActionBtn: { flex: 1, padding: "12px 0", background: "#fff5f5", color: "#E24B4A", border: "1.5px solid #fecaca", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  blockActionBtn:  { flex: 1, padding: "12px 0", background: "#1f2937", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
};

/* ── 팀원 카드 (팀 정보 모달 내부) ─────────────────── */
function ModalMemberCard({ member, sportsType, hostTeam, onClick }) {
  const { profile, sport } = member;
  const nick      = profile?.nickname || member.team_nick || "(닉네임 없음)";
  const initials  = nick[0]?.toUpperCase() || "?";
  const isLeader  = hostTeam?.user_id === member.user_id;
  const level     = formatSportDetail(sportsType, sport?.career_detail);
  const isPlayerLabel = sport ? (sport.is_player ? "선출" : "비선출") : null;
  const detail    = [level, isPlayerLabel].filter(Boolean).join(" · ");
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "#f9fafb", borderRadius: 10, marginBottom: 6, border: "1px solid #f3f4f6", cursor: "pointer" }}
      onClick={onClick}
    >
      {profile?.profile_image ? (
        <img src={profile.profile_image} alt="profile" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
      ) : (
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#E8412A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{initials}</span>
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{nick}</span>
          {isLeader && (
            <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: "#E8412A", borderRadius: 4, padding: "1px 6px" }}>팀장</span>
          )}
        </div>
        {detail && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{detail}</div>}
      </div>
    </div>
  );
}

/* ── 팀원 상세 프로필 바텀시트 ─────────────────────── */
function ModalMemberProfile({ member, profile, sports, loading, sportsType, onClose }) {
  const nick       = profile?.nickname || member.team_nick || "(닉네임 없음)";
  const initials   = nick[0]?.toUpperCase() || "?";
  const genderChip = profile?.gender === "male" ? "남성" : profile?.gender === "female" ? "여성" : null;
  const genderFull = profile?.gender === "male" ? "남자" : profile?.gender === "female" ? "여자" : null;
  const sp         = sports?.[0] || null;
  const spLabel    = sp ? (SPORTS_LABEL_MAP[sp.sports_type] || sp.sports_type) : null;
  const spIcon     = sp ? (SPORTS_ICON_MAP[sp.sports_type] || "🏅") : null;
  const spLevel    = sp ? formatSportDetail(sp.sports_type, sp.career_detail) : null;

  return (
    <div style={ms.backdrop} onClick={onClose}>
      <div style={ms.sheet} onClick={e => e.stopPropagation()}>
        <button style={ms.closeBtn} onClick={onClose}>✕</button>
        <div style={ms.profileTop}>
          {profile?.profile_image ? (
            <img src={profile.profile_image} alt="profile" style={ms.avatar} />
          ) : (
            <div style={ms.avatarFallback}><span style={ms.avatarInitials}>{initials}</span></div>
          )}
          <div style={ms.nickName}>{nick}</div>
          <div style={ms.metaRow}>
            {genderChip  && <span style={ms.metaChip}>{genderChip}</span>}
            {profile?.region && <span style={ms.metaChip}>📍 {profile.region}</span>}
          </div>
          {profile?.awards && <div style={ms.awards}>🏆 {profile.awards}</div>}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#9ca3af", marginBottom: 10 }}>선수 정보</div>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "16px 0" }}>
            <div style={{ width: 24, height: 24, borderRadius: "50%", border: "3px solid #f0f0f0", borderTop: "3px solid #E8412A", animation: "pdSpin 0.8s linear infinite" }} />
          </div>
        ) : !sp ? (
          <div style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "12px 0" }}>등록된 종목 프로필이 없어요</div>
        ) : (
          <div style={{ background: "#F5F5F7", borderRadius: 10, padding: "0 14px" }}>
            <MSRow label="종목" value={`${spIcon} ${spLabel}`} />
            {spLevel && <MSRow label="레벨" value={spLevel} />}
            <MSRow label="선수 출신" value={sp.is_player ? "선출" : "비선출"} />
            {genderFull && <MSRow label="성별" value={genderFull} />}
          </div>
        )}
      </div>
    </div>
  );
}

function MSRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 0", borderBottom: "1px solid #ebebf0" }}>
      <span style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#374151", fontWeight: 600, textAlign: "right", marginLeft: 8 }}>{value}</span>
    </div>
  );
}

const ms = {
  backdrop:       { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 250, padding: "0 20px" },
  sheet:          { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 360, padding: "24px 20px 28px", position: "relative", maxHeight: "85vh", overflowY: "auto", fontFamily: "'Noto Sans KR', sans-serif", boxShadow: "0 8px 32px rgba(0,0,0,0.20)" },
  closeBtn:       { position: "absolute", top: 16, right: 16, background: "none", border: "none", fontSize: 18, color: "#6b7280", cursor: "pointer", lineHeight: 1 },
  profileTop:     { display: "flex", flexDirection: "column", alignItems: "center", paddingBottom: 20, borderBottom: "1px solid #f3f4f6", marginBottom: 16 },
  avatar:         { width: 72, height: 72, borderRadius: "50%", objectFit: "cover", marginBottom: 12 },
  avatarFallback: { width: 72, height: 72, borderRadius: "50%", background: "#E8412A", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 },
  avatarInitials: { fontSize: 28, fontWeight: 800, color: "#fff" },
  nickName:       { fontSize: 20, fontWeight: 800, color: "#1A1A2E", marginBottom: 8 },
  metaRow:        { display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center", marginBottom: 4 },
  metaChip:       { padding: "3px 10px", background: "#F5F5F7", borderRadius: 999, fontSize: 12, color: "#374151", fontWeight: 500 },
  awards:         { fontSize: 12, color: "#d97706", marginTop: 6 },
};
